import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { createCompany, fetchCompanies } from "./companiesSlice";
import CompanyFormFields, {
  buildEmptyValues,
  normalizePayload,
} from "./CompanyFormFields";

const validation = Yup.object({
  name: Yup.string().trim().required("Name is required"),
});

export default function CompanyFormDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { saving, statuses, query } = useSelector((s) => s.companies);

  const formik = useFormik({
    initialValues: buildEmptyValues(),
    validationSchema: validation,
    onSubmit: async (values) => {
      const res = await dispatch(createCompany(normalizePayload(values)));
      if (createCompany.fulfilled.match(res)) {
        toast.success("Company created");
        onClose();
        dispatch(fetchCompanies(query));
        if (res.payload?._id) navigate(`/companies/${res.payload._id}/details`);
      } else {
        toast.error(res.payload || "Save failed");
      }
    },
  });

  useEffect(() => {
    if (open) formik.resetForm({ values: buildEmptyValues() });
    // eslint-disable-next-line
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "2px" } }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h5">New Company</Typography>
          <Typography variant="caption" color="text.secondary">
            Fill the form to onboard a new company. Photos can be added after creation.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: 1.5 }}>
          <CompanyFormFields formik={formik} statuses={statuses} />
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.25,
            borderTop: "1px solid",
            borderColor: "divider",
            position: isMobile ? "sticky" : "static",
            bottom: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Button onClick={onClose} variant="outlined" sx={{ flex: { xs: 1, sm: "0 0 auto" } }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ flex: { xs: 1, sm: "0 0 auto" } }}
            >
              {saving ? "Saving…" : "Create"}
            </Button>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
